import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { uploadAudio } from '../api/transcriptionService';
import { useNotification } from './NotificationContext';

const TranscriptionContext = createContext();

export const useTranscription = () => useContext(TranscriptionContext);

export const TranscriptionProvider = ({ children }) => {
  const [transcriptionJobs, setTranscriptionJobs] = useState({});
  const subscriptionsRef = useRef({}); // Refs para não re-renderizar
  const { showNotification } = useNotification();

  const [synthesisData, setSynthesisData] = useState({ isOpen: false, text: '', patientId: null, sessionNumber: null, roomId: null, sessionLabel: null });

  // RECUPERAÇÃO DE JOBS AO INICIAR
  useEffect(() => {
    try {
      const savedJobs = localStorage.getItem('activeTranscriptionJobs');
      if (savedJobs) {
        const parsedJobs = JSON.parse(savedJobs);
        console.log('[TranscriptionContext] Recovered jobs from localStorage:', parsedJobs);
        setTranscriptionJobs(parsedJobs);
      }
    } catch (e) {
      console.error('[TranscriptionContext] Error recovering jobs:', e);
    }
  }, []);

  // PERSISTÊNCIA DE JOBS
  useEffect(() => {
    if (Object.keys(transcriptionJobs).length > 0) {
      localStorage.setItem('activeTranscriptionJobs', JSON.stringify(transcriptionJobs));
    } else {
      localStorage.removeItem('activeTranscriptionJobs');
    }
  }, [transcriptionJobs]);

  // meta (opcional): { roomId, sessionLabel } quando a transcrição vem de uma teleconsulta
  const openSynthesisWithText = (text, patientId, sessionNumber, meta = {}) => {
    setSynthesisData({ isOpen: true, text, patientId, sessionNumber, roomId: meta.roomId || null, sessionLabel: meta.sessionLabel || null });
  };

  const closeSynthesisModal = () => {
    setSynthesisData({ isOpen: false, text: '', patientId: null, sessionNumber: null, roomId: null, sessionLabel: null });
  };

  const handleSynthesisAction = (data) => {
    if (data.originURL) {
      window.history.pushState({}, '', data.originURL);
      setTimeout(() => {
        openSynthesisWithText(data.transcriptionText || data.text, data.patientId, data.sessionNumber);
      }, 100);
    } else {
      openSynthesisWithText(data.transcriptionText || data.text, data.patientId, data.sessionNumber);
    }
  };

  // GERENCIAMENTO DE SUBSCRIPTIONS (SEPARADO DO ESTADO)
  useEffect(() => {
    Object.keys(transcriptionJobs).forEach(jobId => {
      // Se já temos um listener ATIVO para este job, ignoramos
      if (subscriptionsRef.current[jobId]) return;

      console.log(`[TranscriptionContext] 🎧 Subscribing to job: ${jobId}`);

      const docRef = doc(db, 'transcriptions', jobId);
      const unsubscribe = onSnapshot(docRef, (doc) => {
        const data = doc.data();
        if (!data) return;

        console.log(`[TranscriptionContext] 📥 Update for ${jobId}: ${data.status}`);

        // Atualiza o estado APENAS se houve mudança real para evitar loops
        setTranscriptionJobs(prev => {
          const currentJob = prev[jobId];
          // Se nada mudou, retorna o estado anterior (não dispara re-render)
          if (currentJob?.status === data.status && currentJob?.error === (data.error || null)) {
            return prev;
          }

          return {
            ...prev,
            [jobId]: {
              ...prev[jobId],
              status: data.status,
              error: data.error || null,
              transcriptionText: data.text || ''
            }
          };
        });

        // Lógica de Notificação e Limpeza
        if (data.status === 'completed' || data.status === 'failed') {
          const fileName = data.originalFilename || 'arquivo';

          // Verifica se já notificamos (evita flood) - Usando ref ou verificação de estado anterior
          // Como o listener roda fora do escopo de renderização, acessamos o estado via setTranscriptionJobs ou lógica local
          // Simplificação: Dispara notificação sempre que chega update completed/failed

          if (data.status === 'completed') {
            const action = {
              label: 'Usar para Síntese',
              onClick: () => handleSynthesisAction({ ...data, transcriptionText: data.text })
            };
            showNotification(`Transcrição de "${fileName}" concluída.`, 'success', action);
          } else if (data.status === 'failed') {
            const detailedError = data.error || 'Erro desconhecido no servidor.';
            showNotification(`Falha na transcrição: ${detailedError}`, 'error');
          }

          // Limpeza após 5 segundos
          setTimeout(() => {
            console.log(`[TranscriptionContext] 🧹 Cleaning up job: ${jobId}`);
            if (subscriptionsRef.current[jobId]) {
              subscriptionsRef.current[jobId](); // Para o listener
              delete subscriptionsRef.current[jobId]; // Remove do ref
            }
            setTranscriptionJobs(prev => {
              const next = { ...prev };
              delete next[jobId]; // Remove do estado
              return next;
            });
          }, 5000);
        }
      }, (error) => {
        console.error(`[TranscriptionContext] ❌ Snapshot error for ${jobId}:`, error);
      });

      // Armazena o unsubscribe no REF
      subscriptionsRef.current[jobId] = unsubscribe;
    });

    // Função de limpeza gera: Unsubscribe apenas quando o componente desmontar
    // (Ou quando quisermos forçar, mas aqui deixamos o array de dependências controlar a INSERÇÃO)
    // A remoção individual é feita no callback do snapshot.

    // Nota: Não retornamos cleanup global aqui que remove tudo, pois queremos manter os listeners ativos
    // enquanto o job estiver no estado. O clique "Refresh" do navegador mata tudo de qualquer jeito.

  }, [transcriptionJobs, showNotification]); // Dependência: Apenas quando a lista de jobs muda

  const startTranscription = async (audioFile, patientId, sessionNumber, originURL) => {
    try {
      console.log('[TranscriptionContext] 🚀 Requesting upload...');
      const response = await uploadAudio(audioFile, patientId, sessionNumber, originURL);
      const { transcriptionId } = response.data;
      console.log(`[TranscriptionContext] ✅ Upload done. Job ID: ${transcriptionId}`);

      if (transcriptionId) {
        setTranscriptionJobs(prev => ({
          ...prev,
          [transcriptionId]: {
            status: 'processing',
            originalFilename: audioFile.name,
            patientId,
            originURL
          }
        }));
      }
      return transcriptionId;
    } catch (error) {
      console.error('[TranscriptionContext] ❌ Upload failed:', error);
      showNotification('Erro ao enviar áudio.', 'error');
      throw error;
    }
  };

  return (
    <TranscriptionContext.Provider value={{ startTranscription, transcriptionJobs, synthesisData, closeSynthesisModal, openSynthesisWithText }}>
      {children}
    </TranscriptionContext.Provider>
  );
};